import { Controller } from "@hotwired/stimulus";
import channelService from "../../../services/ChannelService";
import { emitCheckoutEvent } from "../../../services/checkoutEvents";
import { storage } from "../../../storageconstants";
import { debugLog } from "../../../utils";
import { showErrorCard } from "../../../errorsurfaces";
import { PAYMENT_STATUS_SLOW_POLL_MS, paymentStatusAction, paymentStatusPollDelayMs } from "./paymentStatus";
import { embedMessageAction } from "./embedMessage";

/** Ms without refocusing checkout after opening WM partner tab → checkout_wm_tab_abandoned (aligns with partner link expiry). */
const WM_TAB_RETURN_TIMEOUT_MS = 15 * 60 * 1000;

/** Ms to wait for the embedded WaaS iframe before opening a backup tab (iframe stays visible). */
const IFRAME_LOAD_TIMEOUT_MS = 45 * 1000;

/**
 * Two-tier onramp wait timeout (COI-365). The trigger for "waiting" is the return from the provider,
 * but the FIRST webhook is the only proof the deposit is real (vs a failed/abandoned attempt):
 *  - Confirming: returned from provider, no webhook yet → short wait, then offer a retry.
 *  - Finalizing: a webhook proved it → longer wait (it's settling), then "taking longer" + support.
 */
const ONRAMP_CONFIRMING_TIMEOUT_MS = 3 * 60 * 1000;
const ONRAMP_FINALIZING_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * AddFundsWalletController — manages the "Add funds to wallet" step.
 *
 * Embeds the WaaS deposit chooser inline via the partner magic link. The iframe
 * stays visible while the shopper pays (Meld/Stripe may open in another tab).
 * Checkout auto-advances on waas_funded / onramp_received via Pusher, and polls the
 * payment status so a lost webhook or a missed Pusher event cannot hang the page.
 */
export default class AddFundsWalletController extends Controller {
  static values = {
    channelid: String,
    addFundsBalanceUrl: String,
    paymentStatusUrl: String,
    partnerOnrampUrl: String,
    session: String,
    asset: String,
    onrampPending: Boolean,
    fromOnramp: Boolean,
    onrampFailed: Boolean, // server saw the latest Meld attempt fail (COI-833) — open on the retry view
  };

  static targets = [
    "backButton",
    "pageTitle",
    "pageSubtitle",
    "purchaseShortfall",
    "topUpNotice",
    "balanceCheckActions",
    "embedView",
    "iframeLoading",
    "onrampFrame",
    "waitingView",
    "consolidatingView",
    "timeoutView",
    "timeoutTitle",
    "timeoutMessage",
    "retryButton",
    "chooseAnotherButton",
    "fallbackLink",
  ];

  connect() {
    if (!sessionStorage.getItem(storage.WM_FUNNEL_START_MS)) {
      sessionStorage.setItem(storage.WM_FUNNEL_START_MS, String(Date.now()));
    }

    this._allowedWaasOrigin = this.resolveAllowedWaasOrigin();
    this._onPostMessage = (e) => this.handlePostMessage(e);
    globalThis.addEventListener("message", this._onPostMessage);

    // Back from the provider via the browser's back/forward cache, the page would resume a stale
    // pay screen. Reload so the server decides: payment in progress, failed, or pay.
    this._onPageShow = (e) => {
      if (e.persisted) globalThis.location.reload();
    };
    globalThis.addEventListener("pageshow", this._onPageShow);

    if (this.onrampFailedValue) {
      // The provider attempt already failed (webhook processed before this render) — nothing to
      // wait for, go straight to retry / choose another method.
      this.showFailedView();
    } else if (this.onrampPendingValue) {
      // First webhook already proved a real pending deposit — show the finalizing spinner with the
      // long timeout (it's settling, just slow). No iframe: the shopper waits it out.
      this.showConsolidatingView();
      this._startOnrampTimeout(false);
    } else if (this.fromOnrampValue) {
      // Returned from the provider but no webhook yet — optimistically show the "confirming" spinner
      // with the SHORT timeout. If no webhook proves it, we assume the attempt failed/was abandoned
      // and offer a retry. Don't start a second onramp iframe here.
      this.showConfirmingView();
      this._startOnrampTimeout(true);
    } else {
      this.startEmbeddedOnramp();
    }

    this._sawPending = this.onrampPendingValue;
    if (!this.onrampFailedValue) {
      this._startPaymentStatusPoll();
    }

    if (this.channelidValue) {
      channelService.connect(this.channelidValue);

      this._onWaasFunded = (e) => this.handleWaasFunded(e);
      globalThis.addEventListener("waas:funded", this._onWaasFunded);

      // A webhook is the proof the deposit is real: switch to the finalizing spinner and reset to the
      // long timeout. This also recovers if the optimistic timeout view is already showing.
      this._onOnrampReceived = () => {
        this.showConsolidatingView();
        this._startOnrampTimeout(false);
      };
      globalThis.addEventListener("waas:onramp_received", this._onOnrampReceived);

      this._onOnrampPending = () => {
        this.showOnrampPendingView();
        this._startOnrampTimeout(false);
      };
      globalThis.addEventListener("waas:onramp_pending", this._onOnrampPending);

      // Meld's failed webhook (declined card, KYC rejection, provider cancel): stop waiting and
      // offer the way out immediately. Only while we're actually waiting — a late failure event
      // from an earlier attempt must not yank the deposit chooser away mid-flow.
      this._onOnrampFailed = () => {
        if (!this._waiting) return;
        this.showFailedView();
      };
      globalThis.addEventListener("waas:onramp_failed", this._onOnrampFailed);

      debugLog("AddFundsWallet: subscribed to channel", this.channelidValue);
    }
  }

  disconnect() {
    this.clearIframeLoadTimeout();
    this.clearWMTabAbandonWatch();
    this._clearOnrampTimeout();
    this._stopPaymentStatusPoll();

    if (this._onPostMessage) {
      globalThis.removeEventListener("message", this._onPostMessage);
    }
    if (this._onPageShow) {
      globalThis.removeEventListener("pageshow", this._onPageShow);
    }
    if (this._onWaasFunded) {
      globalThis.removeEventListener("waas:funded", this._onWaasFunded);
    }
    if (this._onOnrampReceived) {
      globalThis.removeEventListener("waas:onramp_received", this._onOnrampReceived);
    }
    if (this._onOnrampPending) {
      globalThis.removeEventListener("waas:onramp_pending", this._onOnrampPending);
    }
    if (this._onOnrampFailed) {
      globalThis.removeEventListener("waas:onramp_failed", this._onOnrampFailed);
    }
  }

  startEmbeddedOnramp() {
    if (!this.hasPartnerOnrampUrlValue || !this.partnerOnrampUrlValue) {
      this.fallbackToNewTab("missing_partner_url");
      return;
    }
    if (!this.hasOnrampFrameTarget) {
      this.fallbackToNewTab("missing_iframe");
      return;
    }

    this._waiting = false;
    this.clearIframeLoadTimeout();
    this._iframeLoadTimeout = globalThis.setTimeout(() => {
      debugLog("AddFundsWallet: iframe load timed out");
      this.fallbackToNewTab("load_timeout");
    }, IFRAME_LOAD_TIMEOUT_MS);

    this.onrampFrameTarget.src = this.partnerOnrampUrlValue;
  }

  handleIframeLoad() {
    this.clearIframeLoadTimeout();

    if (this.hasIframeLoadingTarget) {
      this.iframeLoadingTarget.classList.add("hidden");
    }
    if (this.hasOnrampFrameTarget) {
      this.onrampFrameTarget.classList.remove("hidden");
    }
  }

  handlePostMessage(event) {
    switch (embedMessageAction(event, this._allowedWaasOrigin)) {
      case "method_selected":
        this.recordPartnerRedirect(event.data.href || this.partnerOnrampUrlValue);
        break;
      case "payment_in_progress":
        // The embed refused a second payment: one is already pending for this checkout.
        this._sawPending = true;
        this.showOnrampPendingView();
        this._startOnrampTimeout(false);
        this._startPaymentStatusPoll();
        break;
    }
  }

  // showOnrampPendingView locks the page while an onramp is in flight: the
  // onramp iframe is hidden so the shopper can't start another deposit, and the
  // page advances on its own once the deposit settles (waas_funded or the status poll).
  showOnrampPendingView() {
    this._beginWaiting();
    this.clearIframeLoadTimeout();
    this.hidePurchaseShortfall();
    this._hideTimeoutView();

    // Lock the shopper in: an onramp is in flight, so they can't go back and
    // pick a different payment option.
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.add("hidden");
    }
    if (this.hasEmbedViewTarget) {
      this.embedViewTarget.classList.add("hidden");
    }
    if (this.hasWaitingViewTarget) {
      this.waitingViewTarget.classList.add("hidden");
    }
    if (this.hasPageTitleTarget) {
      this.pageTitleTarget.textContent = "Payment in progress";
    }
    this._setSubtitle("Funds detected. Confirming your deposit. This usually takes 2–3 minutes.");
    if (this.hasConsolidatingViewTarget) {
      this.consolidatingViewTarget.classList.remove("hidden");
    }
  }

  showConsolidatingView() {
    this._beginWaiting();
    this.hidePurchaseShortfall();
    this._hideTimeoutView();
    if (this.hasWaitingViewTarget) {
      this.waitingViewTarget.classList.add("hidden");
    }
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.add("hidden");
    }
    if (this.hasEmbedViewTarget) {
      this.embedViewTarget.classList.add("hidden");
    }
    if (this.hasPageTitleTarget) {
      this.pageTitleTarget.textContent = "Payment in progress";
    }
    this._setSubtitle("Funds detected. Confirming your deposit. This usually takes 2–3 minutes.");
    if (this.hasConsolidatingViewTarget) {
      this.consolidatingViewTarget.classList.remove("hidden");
    }
  }

  // showConfirmingView is the optimistic post-provider state: the shopper is back from the provider
  // but no webhook has proven the deposit yet. Same spinner as finalizing, softer copy.
  showConfirmingView() {
    this._beginWaiting();
    this.clearIframeLoadTimeout();
    this.hidePurchaseShortfall();
    this._hideTimeoutView();
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.add("hidden");
    if (this.hasEmbedViewTarget) this.embedViewTarget.classList.add("hidden");
    if (this.hasWaitingViewTarget) this.waitingViewTarget.classList.add("hidden");
    if (this.hasPageTitleTarget) this.pageTitleTarget.textContent = "Confirming your payment";
    this._setSubtitle("This usually takes a minute — we'll continue automatically once it arrives.");
    if (this.hasConsolidatingViewTarget) this.consolidatingViewTarget.classList.remove("hidden");
  }

  // showTimeoutView surfaces after the wait times out. optimistic=true → no webhook ever proved the
  // deposit (likely a failed/abandoned attempt) → offer a retry with the same email. optimistic=false
  // → a webhook proved it but it's slow → keep it soft, no retry (funds are coming; a retry would
  // start a second onramp).
  showTimeoutView(optimistic) {
    this.clearIframeLoadTimeout();
    this.hidePurchaseShortfall();
    if (this.hasEmbedViewTarget) this.embedViewTarget.classList.add("hidden");
    if (this.hasWaitingViewTarget) this.waitingViewTarget.classList.add("hidden");
    if (this.hasConsolidatingViewTarget) this.consolidatingViewTarget.classList.add("hidden");
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.add("hidden");
    if (this.hasPageTitleTarget) {
      this.pageTitleTarget.textContent = optimistic ? "Confirming your payment" : "Almost there";
    }
    this._setSubtitle("");

    if (this.hasTimeoutTitleTarget) {
      this.timeoutTitleTarget.textContent = optimistic
        ? "We haven't received your payment yet"
        : "This is taking longer than expected";
    }
    if (this.hasTimeoutMessageTarget) {
      this.timeoutMessageTarget.textContent = optimistic
        ? "If you completed the payment it can take a moment to arrive. Check again, or try once more with the same email."
        : "Your payment is confirmed and still settling. Check again in a moment, or contact support if it doesn't clear.";
    }
    if (this.hasRetryButtonTarget) {
      this.retryButtonTarget.classList.toggle("hidden", !optimistic);
    }
    if (this.hasChooseAnotherButtonTarget) {
      this.chooseAnotherButtonTarget.classList.toggle("hidden", !optimistic);
    }
    if (this.hasTimeoutViewTarget) this.timeoutViewTarget.classList.remove("hidden");
  }

  // showFailedView: the provider reported the attempt failed (declined card, KYC, cancel), so
  // there is nothing to confirm. Same panel as the timeout view, definite copy, both ways out:
  // retry on the same wallet, or go back and pick another payment method (COI-833).
  showFailedView() {
    this._waiting = false;
    this._clearOnrampTimeout();
    this._stopPaymentStatusPoll();
    this.clearWMTabAbandonWatch();
    this.clearIframeLoadTimeout();
    this.hidePurchaseShortfall();
    if (this.hasEmbedViewTarget) this.embedViewTarget.classList.add("hidden");
    if (this.hasWaitingViewTarget) this.waitingViewTarget.classList.add("hidden");
    if (this.hasConsolidatingViewTarget) this.consolidatingViewTarget.classList.add("hidden");
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.add("hidden");
    if (this.hasPageTitleTarget) this.pageTitleTarget.textContent = "Payment not completed";
    this._setSubtitle("");
    if (this.hasTimeoutTitleTarget) {
      this.timeoutTitleTarget.textContent = "Your payment didn't go through";
    }
    if (this.hasTimeoutMessageTarget) {
      this.timeoutMessageTarget.textContent =
        "The card provider didn't complete this payment, so nothing was charged to your wallet. You can try again or choose another way to pay.";
    }
    if (this.hasRetryButtonTarget) this.retryButtonTarget.classList.remove("hidden");
    if (this.hasChooseAnotherButtonTarget) this.chooseAnotherButtonTarget.classList.remove("hidden");
    if (this.hasTimeoutViewTarget) this.timeoutViewTarget.classList.remove("hidden");
    emitCheckoutEvent("checkout_wm_onramp_failed", {});
  }

  // retryOnramp restarts the onramp on the same session/wallet: reload the add-funds page WITHOUT
  // from_onramp so the deposit chooser loads again. The wallet is deterministic per account, so any
  // late funds from the earlier attempt still land here — retrying can't strand money or send it to
  // a different wallet.
  retryOnramp(event) {
    event?.preventDefault();
    const u = new URL(globalThis.location.href);
    u.searchParams.delete("from_onramp");
    u.searchParams.set("_bc", String(Date.now()));
    globalThis.location.assign(u.toString());
  }

  _startOnrampTimeout(optimistic) {
    this._clearOnrampTimeout();
    const ms = optimistic ? ONRAMP_CONFIRMING_TIMEOUT_MS : ONRAMP_FINALIZING_TIMEOUT_MS;
    this._onrampTimeout = globalThis.setTimeout(() => {
      this._onrampTimeout = null;
      this.showTimeoutView(optimistic);
    }, ms);
  }

  // _beginWaiting marks the page as waiting on a payment and starts the poll clock on the first
  // call, so polling switches from the idle cadence to the fast one straight away.
  _beginWaiting() {
    this._waiting = true;
    if (this._waitingSinceMs) return;
    this._waitingSinceMs = Date.now();
    if (this._paymentStatusPollActive) this._scheduleNextPoll(0);
  }

  _startPaymentStatusPoll() {
    if (!this.hasPaymentStatusUrlValue || !this.paymentStatusUrlValue) return;
    this._paymentStatusPollActive = true;
    this._scheduleNextPoll(this._waiting ? 0 : PAYMENT_STATUS_SLOW_POLL_MS);
  }

  _nextPollDelayMs() {
    if (!this._waiting) return PAYMENT_STATUS_SLOW_POLL_MS;
    return paymentStatusPollDelayMs(Date.now() - this._waitingSinceMs);
  }

  _scheduleNextPoll(delayMs) {
    this._clearPaymentStatusTimer();
    if (delayMs === null) {
      this._paymentStatusPollActive = false;
      return;
    }
    this._paymentStatusPoll = globalThis.setTimeout(async () => {
      this._paymentStatusPoll = null;
      try {
        await this._pollPaymentStatus();
      } catch (err) {
        debugLog("AddFundsWallet: status poll failed", err);
      }
      if (this._paymentStatusPollActive && !this._paymentStatusPoll) {
        this._scheduleNextPoll(this._nextPollDelayMs());
      }
    }, delayMs);
  }

  _stopPaymentStatusPoll() {
    this._paymentStatusPollActive = false;
    this._clearPaymentStatusTimer();
  }

  _clearPaymentStatusTimer() {
    if (this._paymentStatusPoll) {
      globalThis.clearTimeout(this._paymentStatusPoll);
      this._paymentStatusPoll = null;
    }
  }

  async _pollPaymentStatus() {
    if (this._statusPollInFlight) return;
    this._statusPollInFlight = true;
    try {
      const url = new URL(this.paymentStatusUrlValue, globalThis.location.href);
      url.searchParams.set("_bc", String(Date.now()));
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const { status } = await res.json();
      this._applyPaymentStatus(status);
    } finally {
      this._statusPollInFlight = false;
    }
  }

  _applyPaymentStatus(status) {
    switch (paymentStatusAction(status, { sawPending: this._sawPending, waiting: this._waiting })) {
      case "advance":
        this._clearOnrampTimeout();
        this.navigateToBalanceCheck("status_poll");
        break;
      case "failed":
        this.showFailedView();
        break;
      case "pending":
        this._sawPending = true;
        if (!this._waiting) {
          this.showConsolidatingView();
          this._startOnrampTimeout(false);
        }
        break;
    }
  }

  _clearOnrampTimeout() {
    if (this._onrampTimeout) {
      globalThis.clearTimeout(this._onrampTimeout);
      this._onrampTimeout = null;
    }
  }

  _hideTimeoutView() {
    if (this.hasTimeoutViewTarget) this.timeoutViewTarget.classList.add("hidden");
  }

  // _setSubtitle sets the first subtitle line and hides any extra subtitle lines (the template has
  // two) so spinner/timeout states don't leave a stray sentence behind.
  _setSubtitle(text) {
    if (!this.hasPageSubtitleTarget) return;
    this.pageSubtitleTargets.forEach((el, i) => {
      if (i === 0) {
        el.textContent = text;
        el.classList.toggle("hidden", !text);
      } else {
        el.classList.add("hidden");
      }
    });
  }

  fallbackToNewTab(reason) {
    debugLog("AddFundsWallet: opening backup onramp tab (iframe stays visible)", { reason });
    this.clearIframeLoadTimeout();

    const url = this.hasPartnerOnrampUrlValue ? this.partnerOnrampUrlValue : "";
    if (!url) {
      // No provider URL at all — the buy-crypto window can never load (ONR-03).
      showErrorCard("buy-unavailable");
      return;
    }

    if (this.hasFallbackLinkTarget) {
      this.fallbackLinkTarget.click();
    } else {
      globalThis.open(url, "_blank", "noopener,noreferrer");
    }

    this.recordPartnerRedirect(url);
  }

  recordPartnerRedirect(href) {
    this.hidePurchaseShortfall();

    let domain = "";
    try {
      domain = new URL(href, globalThis.location.href).hostname || "";
    } catch (_) {
      /* swallow */
    }
    if (!domain) return;

    if (this.hasSessionValue && this.sessionValue) {
      sessionStorage.setItem(storage.SESSION, this.sessionValue);
    }

    sessionStorage.setItem(storage.WM_FUNNEL_START_MS, String(Date.now()));

    emitCheckoutEvent("checkout_wm_redirect", {
      redirect_url_domain: domain,
    });

    this.scheduleWMTabAbandonWatch();
  }

  hidePurchaseShortfall() {
    if (this.hasPurchaseShortfallTarget) {
      this.purchaseShortfallTarget.classList.add("hidden");
    }
    // The top-up notice (COI-833) is the shortfall explanation for the embedded second onramp;
    // once the shopper is redirected to the provider or a spinner state takes over, it's stale.
    if (this.hasTopUpNoticeTarget) {
      this.topUpNoticeTarget.classList.add("hidden");
    }
  }

  navigateToBalanceCheck(source) {
    if (!this.hasAddFundsBalanceUrlValue || !this.addFundsBalanceUrlValue) {
      debugLog("AddFundsWallet: balance check skipped — missing URL", { source });
      return false;
    }
    if (this._navigating) return true;
    this._navigating = true;
    this._stopPaymentStatusPoll();
    this.hidePurchaseShortfall();

    const checkURL = new URL(this.addFundsBalanceUrlValue, globalThis.location.href);
    checkURL.searchParams.set("_bc", String(Date.now()));
    debugLog("AddFundsWallet: navigating to balance check", { source, url: checkURL.toString() });
    globalThis.location.assign(checkURL.toString());
    return true;
  }

  checkBalanceNow(event) {
    event?.preventDefault();
    this.navigateToBalanceCheck("manual_reload");
  }

  resolveAllowedWaasOrigin() {
    if (!this.hasPartnerOnrampUrlValue || !this.partnerOnrampUrlValue) {
      return "";
    }
    try {
      return new URL(this.partnerOnrampUrlValue).origin;
    } catch (_) {
      return "";
    }
  }

  clearIframeLoadTimeout() {
    if (this._iframeLoadTimeout) {
      globalThis.clearTimeout(this._iframeLoadTimeout);
      this._iframeLoadTimeout = null;
    }
  }

  scheduleWMTabAbandonWatch() {
    this.clearWMTabAbandonWatch();
    this._wmRedirectAtMs = Date.now();

    this._wmAbandonTimeout = globalThis.setTimeout(() => {
      const elapsed = Date.now() - (this._wmRedirectAtMs || Date.now());
      emitCheckoutEvent("checkout_wm_tab_abandoned", {
        time_since_redirect_ms: String(elapsed),
      });
      this._wmAbandonTimeout = null;
    }, WM_TAB_RETURN_TIMEOUT_MS);

    this._onWMVisibilityReturn = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        this.clearWMTabAbandonWatch();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this._onWMVisibilityReturn);
    }
  }

  clearWMTabAbandonWatch() {
    if (this._wmAbandonTimeout) {
      globalThis.clearTimeout(this._wmAbandonTimeout);
      this._wmAbandonTimeout = null;
    }
    if (this._onWMVisibilityReturn && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this._onWMVisibilityReturn);
      this._onWMVisibilityReturn = null;
    }
  }

  handleWaasFunded(event) {
    this.clearWMTabAbandonWatch();
    this._clearOnrampTimeout();

    const detail = event.detail || {};
    const balanceUSD = Number.parseFloat(detail.balanceUSD) || 0;
    const prevBal = Number.parseFloat(sessionStorage.getItem(storage.WM_LAST_BALANCE_USD) || "") || 0;
    const purchased =
      balanceUSD > prevBal ? balanceUSD - prevBal : balanceUSD > 0 ? balanceUSD : 0;

    const startMs = Number.parseInt(sessionStorage.getItem(storage.WM_FUNNEL_START_MS) || "", 10);
    const elapsed =
      Number.isFinite(startMs) && startMs > 0 ? Date.now() - startMs : 0;

    const asset = (this.hasAssetValue && this.assetValue ? this.assetValue : "USDC").trim();

    if (this.hasSessionValue && this.sessionValue) {
      sessionStorage.setItem(storage.SESSION, this.sessionValue);
    }

    if (balanceUSD > 0 || purchased > 0) {
      sessionStorage.setItem(storage.WM_LAST_BALANCE_USD, String(balanceUSD));
      emitCheckoutEvent("checkout_wm_balance_updated", {
        amount_purchased_crypto: purchased > 0 ? purchased.toFixed(8) : "",
        asset,
        time_in_wm_ms: elapsed > 0 ? String(elapsed) : "",
      });
    }

    debugLog("AddFundsWallet: waas_funded received", { balanceUSD });

    this.navigateToBalanceCheck("waas_funded");
  }
}
